import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  TrendUp, ShoppingCart, CurrencyDollar, Users, Cube, ArrowsClockwise,
  Warning, CheckCircle, Truck, CaretUp, CaretDown
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function KpiCard({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#9c9db7]">{label}</p>
          <p className="text-2xl font-bold text-[#0A0A0A] mt-1">{value}</p>
          {sub && <p className="text-[11px] text-[#5a5c87] mt-0.5">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon size={20} style={{ color }} weight="bold" />
        </div>
      </div>
    </div>
  );
}

export default function ControlTowerPage() {
  const [ctStats, setCtStats] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
  const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, sellersRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/control-tower/stats`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/sellers?limit=50`, { withCredentials: true }),
        ]);
        setCtStats(statsRes.data);
        setSellers(sellersRes.data.sellers || sellersRes.data || []);
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const sellerHealth = useMemo(() => {
    return sellers
      .filter(s => (s.stats?.total_orders || 0) > 0)
      .map(s => ({
        ...s,
        health: (s.stats?.fulfillment_rate || 0) >= 80 ? 'healthy' :
                (s.stats?.fulfillment_rate || 0) >= 50 ? 'warning' : 'critical'
      }))
      .sort((a, b) => (b.stats?.total_revenue || 0) - (a.stats?.total_revenue || 0));
  }, [sellers]);

  const healthySellers = sellerHealth.filter(s => s.health === 'healthy').length;
  const warningSellers = sellerHealth.filter(s => s.health === 'warning').length;
  const criticalSellers = sellerHealth.filter(s => s.health === 'critical').length;

  const topSellerColumns = [
    { key: 'name', label: 'Seller', render: (v, row) => (
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{
          backgroundColor: row.health === 'healthy' ? '#28cd8c15' : row.health === 'warning' ? '#f6a60915' : '#ea4b5a15',
          color: row.health === 'healthy' ? '#28cd8c' : row.health === 'warning' ? '#f6a609' : '#ea4b5a'
        }}>
          {(v || '?')[0].toUpperCase()}
        </div>
        <div><p className="text-sm font-semibold">{v}</p><p className="text-[10px] text-[#9c9db7]">{row.email}</p></div>
      </div>
    )},
    { key: 'stats_revenue', label: 'Revenue', render: (v, row) => <span className="font-bold text-[#28cd8c]">{fmt(row.stats?.total_revenue)}</span> },
    { key: 'stats_orders', label: 'Orders', render: (v, row) => fmtNum(row.stats?.total_orders) },
    { key: 'stats_products', label: 'Products', render: (v, row) => fmtNum(row.stats?.total_products) },
    { key: 'stats_fulfillment', label: 'Fulfillment', render: (v, row) => {
      const rate = row.stats?.fulfillment_rate || 0;
      const c = rate >= 80 ? '#28cd8c' : rate >= 50 ? '#f6a609' : '#ea4b5a';
      return <span className="font-bold" style={{ color: c }}>{rate}%</span>;
    }},
    { key: 'stats_returns', label: 'Returns', render: (v, row) => {
      const rate = row.stats?.return_rate || 0;
      const c = rate < 5 ? '#28cd8c' : rate < 15 ? '#f6a609' : '#ea4b5a';
      return <span style={{ color: c }}>{rate}%</span>;
    }},
    { key: 'health', label: 'Health', render: v => {
      const c = v === 'healthy' ? '#28cd8c' : v === 'warning' ? '#f6a609' : '#ea4b5a';
      const IconComp = v === 'healthy' ? CheckCircle : Warning;
      return <span className="flex items-center gap-1 text-xs font-bold capitalize" style={{ color: c }}><IconComp size={14} weight="bold" />{v}</span>;
    }},
  ];

  const st = ctStats || {};

  return (
    <DashboardLayout title="Control Tower" subtitle="Marketplace-wide analytics and health monitoring">
      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 skeleton rounded-xl" />)}</div>
          <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-20 skeleton rounded-xl" />)}</div>
          <div className="h-64 skeleton rounded-xl" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Total GMV" value={fmt(st.total_gmv)} icon={CurrencyDollar} color="#28cd8c" testId="ct-stat-gmv" />
            <KpiCard label="Total Orders" value={fmtNum(st.total_orders)} sub={`${fmtNum(st.delivered_orders)} delivered`} icon={ShoppingCart} color="#3b82f6" testId="ct-stat-orders" />
            <KpiCard label="Active Sellers" value={st.active_sellers} sub={`${st.total_sellers} total, ${st.kyc_approved} KYC approved`} icon={Users} color="#7d64ff" testId="ct-stat-sellers" />
            <KpiCard label="Total Products" value={fmtNum(st.total_products)} icon={Cube} color="#f6a609" testId="ct-stat-products" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Avg Fulfillment" value={`${st.avg_fulfillment || 0}%`} icon={Truck} color={(st.avg_fulfillment || 0) >= 80 ? '#28cd8c' : '#f6a609'} testId="ct-stat-fulfillment" />
            <KpiCard label="Total Returns" value={fmtNum(st.total_returns)} sub={`${fmtNum(st.cancelled_orders)} cancelled`} icon={ArrowsClockwise} color="#ea4b5a" testId="ct-stat-returns" />
            <KpiCard label="Total Settled" value={fmt(st.total_settled)} icon={CheckCircle} color="#7d64ff" testId="ct-stat-settled" />
            <KpiCard label="Pending Settlement" value={fmt(st.pending_settlement)} icon={Warning} color="#f6a609" testId="ct-stat-pending" />
          </div>

          <div className="bg-white border border-[#e5e5ea] rounded-xl p-5 mb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#5a5c87] mb-4" data-testid="ct-health-title">Seller Health Distribution</h3>
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#28cd8c]"></div>
                <span className="text-sm">Healthy <strong className="text-[#28cd8c]">{healthySellers}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#f6a609]"></div>
                <span className="text-sm">Warning <strong className="text-[#f6a609]">{warningSellers}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#ea4b5a]"></div>
                <span className="text-sm">Critical <strong className="text-[#ea4b5a]">{criticalSellers}</strong></span>
              </div>
            </div>
            <div className="flex h-3 rounded-full overflow-hidden bg-[#f0f0f0]">
              {healthySellers > 0 && <div className="bg-[#28cd8c]" style={{ width: `${(healthySellers / Math.max(sellerHealth.length, 1)) * 100}%` }}></div>}
              {warningSellers > 0 && <div className="bg-[#f6a609]" style={{ width: `${(warningSellers / Math.max(sellerHealth.length, 1)) * 100}%` }}></div>}
              {criticalSellers > 0 && <div className="bg-[#ea4b5a]" style={{ width: `${(criticalSellers / Math.max(sellerHealth.length, 1)) * 100}%` }}></div>}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#5a5c87] mb-3" data-testid="ct-leaderboard-title">Seller Leaderboard</h3>
            <SortableTable columns={topSellerColumns} data={sellerHealth} pageSize={15} emptyMessage="No active sellers" emptyIcon={Users} />
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
